import { Injectable } from '@angular/core';
import { AppMasterDBFacade, PersistentStorageService } from '@monorepo/core-data';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { from, of } from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs/operators';
import { LoadShifts, LoadShiftsFailed } from '../shifts/shifts.actions';
import { LoadMasterData } from '../master-data-db/master-data-db.actions';
import {
  AppMasterDBActionTypes,
  AppMasterDBLoaded,
  AppSwitch,
  ChangeStation,
  FailedAppMasterDBLoad,
  LoadAppMasterDBSettings,
} from './app-master-db.actions';
import { getNormalizedData } from './app-master.db';
import { TRESPONSE_APP_MASTER } from '@monorepo/types';
import { TPreSettingInfoList } from '@monorepo/resolvers';

@Injectable({ providedIn: 'root' })
export class AppMasterDBEffects {
  constructor(
    private actions$: Actions,
    private persistentStorageService: PersistentStorageService,
    private appMasterDBFacade: AppMasterDBFacade,
  ) {}

  loadAppMasterDB$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppMasterDBActionTypes.LoadAppMasterDB),
      switchMap(() =>
        from(this.persistentStorageService.get('[APP_MASTER]')).pipe(
          map((rootAppInfo) => {
            const normalizedAppMaster = getNormalizedData(rootAppInfo);
            return new AppMasterDBLoaded(normalizedAppMaster);
          }),
          catchError(() => {
            return of(new FailedAppMasterDBLoad());
          }),
        ),
      ),
    ),
  );

  loadMasterData = createEffect(() =>
    this.actions$.pipe(
      ofType(AppMasterDBActionTypes.AppMasterDBLoaded),
      map(() => new LoadMasterData()),
    ),
  );

  appMasterDBLoaded$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppMasterDBActionTypes.AppMasterDBLoaded),
      switchMap((action: AppMasterDBLoaded) =>
        from(this.persistentStorageService.get('[APP_MASTER][USER_SETTING]')).pipe(
          map((as) => {
            if (as) {
              return new LoadAppMasterDBSettings(as);
            } else {
              const normalizedAppMaster = action.payload;
              const appMasterId = normalizedAppMaster.result || '';
              const appId = normalizedAppMaster.entities['APP_MASTER'][appMasterId].apps[0];
              const selectedApp = normalizedAppMaster.entities.APPS[appId];
              const moduleId = selectedApp.appInfo.modules[0];
              const stationId = selectedApp.settingInfo.subjects[0];
              const businessContextId = selectedApp.settingInfo.bctx[0];

              const appSettings = {
                appId,
                moduleId: moduleId,
                stationId,
                businessContextId,
                landingViewDescriptorId: '',
                hasSilhouette: false,
              };
              return new LoadAppMasterDBSettings(appSettings);
            }
          }),
        ),
      ),
    ),
  );

  loadAppMasterDBSettings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppMasterDBActionTypes.LoadAppMasterDBSettings),
      switchMap((action: LoadAppMasterDBSettings) =>
        this.persistentStorageService.set('[APP_MASTER][USER_SETTING]', action.payload),
      ),
      map(() => new LoadShifts()),
    ),
  );

  // loadAppMasterDBSettingsWorkItems$ = createEffect(() =>
  //   this.actions$.pipe(
  //     ofType(AppMasterDBActionTypes.LoadAppMasterDBSettings),
  //     map(() => new LoadWorkItemsDB()),
  //   ),
  // );

  changeStation$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<ChangeStation>(AppMasterDBActionTypes.ChangeStation),
        //never change the below two switch maps order without proper testing
        switchMap((action) =>
          this.appMasterDBFacade.getAppSettings().pipe(
            take(1),
            map((appSetting) => {
              if (appSetting) {
                return {
                  ...appSetting,
                  stationId: action.payload,
                };
              } else {
                return {
                  appId: '',
                  moduleId: '',
                  stationId: action.payload,
                  businessContextId: '',
                  landingViewDescriptorId: '',
                  hasSilhouette: false,
                };
              }
            }),
          ),
        ),
        switchMap((v) =>
          from(this.persistentStorageService.set('[APP_MASTER][USER_SETTING]', v)).pipe(
            map(() => {
              console.log('[APP_MASTER][USER_SETTING] updated');
            }),
            catchError((error) => {
              console.log('[APP_MASTER][USER_SETTING] update failed');
              return of('Error', error);
            }),
          ),
        ),
      ),
    {
      dispatch: false,
    },
  );

  changeApp$ = createEffect(() =>
    this.actions$.pipe(
      ofType<AppSwitch>(AppMasterDBActionTypes.AppSwitch),
      switchMap((action) => {
        return this.appMasterDBFacade.getAllAppsInfo().pipe(
          take(1),
          map((allApps) => {
            return [action, allApps] as const;
          }),
        );
      }),
      //never change the below two switch maps order without proper testing
      switchMap(([action, allApps]) =>
        this.appMasterDBFacade.getAppSettings().pipe(
          take(1),
          map((appSetting) => {
            const appId = action.appId;
            const selectedApp = allApps.find((app) => app.appInfo.app_id === appId) as TPreSettingInfoList[number];
            const moduleId = selectedApp.appInfo.modules[0].module_type;
            const stationId = selectedApp.settingInfo.subjects[0].subject;
            const businessContextId = selectedApp.settingInfo.bctx[0].bctx_type;

            const appSettingsLatest = {
              appId: action.appId,
              moduleId: moduleId,
              stationId,
              businessContextId,
              ...(appSetting
                ? {
                    landingViewDescriptorId: appSetting.landingViewDescriptorId,
                    hasSilhouette: appSetting.hasSilhouette,
                  }
                : {
                    landingViewDescriptorId: '',
                    hasSilhouette: false,
                  }),
            };

            return new LoadAppMasterDBSettings(appSettingsLatest);
          }),
        ),
      ),
    ),
  );
}
